import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from '../../../shared/PolicyDialog/styles';
import { LuiSelect } from '@jda/lui-common-component-library-mui5';
import { Grid, TextField, Typography, Box, Switch, Button, Alert } from '@mui/material';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import {
  POLICY_NAME,
  POLICY_NAME_LABEL,
  POLICY_DESCRIPTION,
  POLICY_DESC_LABEL,
  POLICY_DIMENSION,
  POLICY_HIERARCHY,
  POLICY_ACTIONS_LABEL,
  DROPDOWN_MAX_HEIGHT,
  POLICY_DIMENSION_LABEL,
  DISPLAY_NAME,
  AUTHORIZING_HIERARCHY_DESCRIPTION,
  NO_AUTHORIZING_HIERARCHY,
  POLICY_HIERARCHY_ALL,
  DATA_SCOPE_ACCESS_FOR,
  ENABLE,
  DISABLE,
  OPTIONAL,
  SELECT_ALL,
  POLICY_HIERARCHY_ALL_ACTIONS,
  DIMENSION_ATTRIBUTE_TITLE,
  ADD_ATTRIBUTE_BTN_TEXT,
  ATTR_MAX_COUNT,
  ATTR_MAX_COUNT_WARN,
  ALL_ATTR_LISTED_WARN,
  ATTR_ROWS_CROSSED_MAX_AVAILABLE_WARN,
  ATTR_MAX_COUNT_MSG,
  HIERARCHY_ACCESS_CONTROL,
  NO_DIMENSION_ATTR
} from '../../../shared/PolicyDialog/constants';
import { getDimensions, getHierarchy, getDimensionAttributes } from '../../../store/role/policyDimension/dimension';
import { fetchDataPlatformActions } from '../../../store/role/policy';
import {
  selectDimensionsList,
  selectDimensionPolicyData,
  selectDimensionFormError,
  selectDimensionAttributes
} from '../../../store/role/policyDimension/dimensionSelector';
import {
  selectDataAccessActions,
  selectHierarchyAccessActions,
  selectDimensionAccessActions,
  selectAttributeAccessActions
} from '../../../store/role/roleSelector';
import {
  setDimensionPolicyData,
  setDimensionAttributes,
  setHierarchiesData,
  updateDimensionFormError
} from '../../../store/role/policyDimension/dimensionActionCreator';
import { selectError } from '../../../store/error/errorSelector';
import AccordionListCollector from '../Dimension/AccordionListCollector';
import { compareByProperty } from '../../../utility/commonUtility';
import { ISelect } from '../../../models/Roles/RolesContracts';
import HierarchyTable from './HierarchyTable';
import { getHierarchyForDimension, getAttributes } from '../../../services/policy';
import { setSnackBarMessage } from '../../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../RoleDetails/constants';
import { showLoaderAction } from '../../../store/loader/loaderActionCreator';
import { IPolicy, ISelectedHierarchiesExpression } from '../../../models/Policies/PolicyContract';
import { selectLoader } from '../../../store/loader/loaderSelector';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';
import { hideLoader } from '../../../store/loader/loader';
import { DimensionAttribute } from './DimensionAttribute';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../../utility/constants';

const DimensionV2 = (props: any) => {
  const { setIsSaveDisabled, edit, rowData } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const dimensionAttributeFF = useFeatureFlag(HARNESS_FEATURE_FLAG.dimensionAttributeFF);
  const displayLoader = useSelector(selectLoader);
  const dimensionsList = useSelector(selectDimensionsList);
  const dimensionAttributes = useSelector(selectDimensionAttributes);
  const attributeAccessActions = useSelector(selectAttributeAccessActions);
  const dataAccessActions = useSelector(selectDataAccessActions);
  const hierarchyAccessActions = useSelector(selectHierarchyAccessActions);
  const dimensionAccessActions = useSelector(selectDimensionAccessActions);
  const dimensionPolicyData = useSelector(selectDimensionPolicyData);
  const hierarchyLevels = dimensionPolicyData.hierarchy?.levels;
  const formErrorState = useSelector(selectDimensionFormError);
  const errors = useSelector(selectError);
  const [isOpen, setIsOpen] = useState(false);
  const filteredDimensionAttributes = dimensionAttributes?.filter(
    (item: any) => !dimensionPolicyData.attributes.some((attr: any) => attr?.attribute?.key === item?.key)
  );
  useEffect(() => {
    getDimensions(dispatch);
    fetchDataPlatformActions(dispatch);
  }, [dispatch]);

  const handleRowData = (data: IPolicy) => {
    const expression = JSON.parse(data.expression);

    const dimension = dimensionsList.find((item: any) => item.id === expression.policy.dimension.dimensionId);
    if (!dimension) return;

    const currentDimensionActions = expression.policy.dimension.actions;
    const resultDimensionActions = currentDimensionActions.map((el: any) =>
      dimensionAccessActions.find(item => item.name === el)
    );

    const currentDepthAccessActions = expression.policy.dimension.dataAccess.actions || [];
    const resultDepthAccessActions = currentDepthAccessActions
      .map((el: any) => dataAccessActions.find(item => item.name === el))
      .filter((el: any) => el !== undefined);

    dispatch(showLoaderAction());
    setIsSaveDisabled(true);

    if (expression.policy.dimension.attributeAccess?.length > 0 && dimensionAttributeFF) {
      getAttributes(dimension.id, 'DIMENSION')
        .then((response: any) => {
          const { attributes } = response;
          const dimAttributes = attributes.map((attribute: any) => ({
            displayName: attribute.label,
            key: attribute.id
          }));
          const updatedAttributes = expression.policy.dimension.attributeAccess.map((attr: any) => {
            const attribute = dimAttributes.find((item: any) => item.key === attr.attribute);
            const attributeAction = attr.actions.map((action: any) =>
              attributeAccessActions.find(item => item.name === action)
            );
            return {
              attribute,
              attributeAction
            };
          });
          dispatch(setDimensionAttributes(dimAttributes));
          dispatch(setDimensionPolicyData('attributes', updatedAttributes));
        })
        .catch(error => {
          setIsSaveDisabled(true);
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: 'Something went wrong. ' + error.message,
              severity: 'error'
            })
          );
        });
    } else {
      getDimensionAttributes(dispatch, dimension.id);
    }
    getHierarchyForDimension(dimension.id)
      .then(response => {
        dispatch(setDimensionPolicyData(POLICY_NAME, expression.displayName));
        dispatch(setDimensionPolicyData(POLICY_DESCRIPTION, expression.description));
        dispatch(setDimensionPolicyData(POLICY_DIMENSION, dimension));
        dispatch(setDimensionPolicyData('dimensionAction', resultDimensionActions));
        dispatch(setDimensionPolicyData('action', resultDepthAccessActions));

        if (response.entities) {
          const selectedHierarchyIds: ISelectedHierarchiesExpression[] =
            expression.policy.dimension.hierarchyAccess.hierarchies || [];
          const heirarchy = response.entities?.find((item: any) => item.useForAuthorization);
          const hierarchiesAll = [...response.entities];

          dispatch(setDimensionPolicyData(POLICY_HIERARCHY_ALL, hierarchiesAll));
          dispatch(setDimensionPolicyData(POLICY_HIERARCHY, heirarchy || null));

          if (selectedHierarchyIds.length) {
            hierarchiesAll.forEach(item => {
              const currentLevel = [...item.levels, { id: '*', label: SELECT_ALL }].find(level =>
                selectedHierarchyIds.some(elem => elem.depthAccess === level.id && item.id === elem.id)
              );

              if (currentLevel) dispatch(setHierarchiesData('levels', item.id, currentLevel));
            });

            function findActionsForId(id: any) {
              const hierarchy = selectedHierarchyIds.find(h => h.id === id);
              return hierarchy ? hierarchy.actions : [];
            }

            hierarchiesAll.forEach(hierarchy => {
              const actions = findActionsForId(hierarchy.id);
              const actionDetails = actions.map((actionName: any) =>
                hierarchyAccessActions.find(action => action.name === actionName)
              );

              actionDetails.length && dispatch(setHierarchiesData('actions', hierarchy.id, actionDetails));
            });
          }

          const hierarchyId = expression.policy.dimension.dataAccess.hierarchyId;
          const hierarchyFilter = expression.policy.dimension.dataAccess.hierarchyFilter;

          let isOpenFlag = false;
          if (hierarchyId && hierarchyFilter) {
            const currentHierarchyForLevels = hierarchiesAll.find((item: any) => item.id === hierarchyId);
            const member: any = {};
            currentHierarchyForLevels.levels.forEach((level: any) => {
              const { id } = level;
              const value = hierarchyFilter[id];

              if (Array.isArray(value)) {
                member[id] = value.map(item => ({
                  memberLabel: item,
                  value: item
                }));
                isOpenFlag = true;
              } else if (typeof value === 'string' && value !== '') {
                member[id] = [{ memberLabel: value, value: value }];
                isOpenFlag = true;
              }
            });

            dispatch(setDimensionPolicyData('members', member));
          }
          setIsSaveDisabled(true);
          dispatch<any>(hideLoader);
          setIsOpen(isOpenFlag);
        }
      })
      .catch(error => {
        setIsSaveDisabled(true);
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: 'Something went wrong. ' + error.message,
            severity: 'error'
          })
        );
      });
  };

  useEffect(() => {
    if (edit && rowData && dimensionsList) {
      handleRowData(rowData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, rowData, dimensionsList]);

  const handleSaveButtonDisable = useCallback(() => {
    if (
      dimensionPolicyData.policyName !== '' &&
      dimensionPolicyData.policyDescription !== '' &&
      dimensionPolicyData.dimension &&
      dimensionPolicyData.dimensionAction.length !== 0
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [dimensionPolicyData, setIsSaveDisabled]);

  useEffect(() => {
    handleSaveButtonDisable();
  }, [handleSaveButtonDisable]);

  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    dispatch(setDimensionPolicyData(e.target.name, e.target.value));
    dispatch(updateDimensionFormError(e.target.name, ''));
  };

  const onDimensionChange = (evt: any) => {
    dispatch(setDimensionPolicyData(POLICY_DIMENSION, evt.target.value));
    dispatch(setDimensionPolicyData(POLICY_HIERARCHY, null));
    dispatch(setDimensionPolicyData(POLICY_HIERARCHY_ALL, null));
    dispatch(setDimensionPolicyData(POLICY_HIERARCHY_ALL_ACTIONS, {}));
    dispatch(setDimensionPolicyData('members', null));
    getDimensionAttributes(dispatch, evt.target.value.id);
    getHierarchy(dispatch, evt.target.value.id);
  };

  const getHierarchyMap = (hierarchyLevels: any[]) => {
    let disabled = false;
    return hierarchyLevels.map((level: any, index: number, levels: any[]) => {
      if (index > 0) {
        // When the previous level dimension has more than one selected item the next level dimesions should be disabled
        disabled =
          dimensionPolicyData?.members === null ||
          dimensionPolicyData?.members[levels[index - 1]?.id] === undefined ||
          dimensionPolicyData?.members[levels[index - 1]?.id][0]?.value === '' ||
          dimensionPolicyData?.members[levels[index - 1]?.id][0]?.value === null ||
          dimensionPolicyData?.members[levels[index - 1]?.id]?.length !== 1;
      }
      return renderAccordionListCollector(level, index, disabled);
    });
  };

  const renderAccordionListCollector = (level: any, index: number, disabled: boolean) => {
    const props = {
      ...level,
      index,
      disabled: dimensionPolicyData.selectAllDimension || disabled,
      dimensionId: dimensionPolicyData.dimension.id
    };

    return <AccordionListCollector {...props} />;
  };

  const handleActionChange = (name: string, e: ISelect[]) => {
    dispatch(setDimensionPolicyData(name, e));
    dispatch(updateDimensionFormError(name, ''));
  };
  const renderHierarchySection = () => {
    return (
      <>
        <Box pt={2} pb={2}>
          <HierarchyTable hierarchyTableData={dimensionPolicyData.hierarchyAll} actionsList={hierarchyAccessActions} />
        </Box>

        {dimensionPolicyData.hierarchy !== null ? (
          <Box className={classes.hierarchyAccessScope}>
            <Box>
              <Typography
                variant="h4"
                className={classes.accessTypography}
              >{`${DATA_SCOPE_ACCESS_FOR} ${dimensionPolicyData.hierarchy.id} (${OPTIONAL})`}</Typography>
              <Typography variant="body2" className={classes.accessTypographyDescription}>
                {AUTHORIZING_HIERARCHY_DESCRIPTION}
              </Typography>
            </Box>
            <Box className={classes.hierarchyAccessSwitch}>
              <Switch
                checked={isOpen}
                onChange={() => {
                  setIsOpen(!isOpen);
                }}
                color="primary"
                name={'switch-hierarchy'}
                size='small'
              />
              <Typography>{isOpen ? DISABLE : ENABLE}</Typography>
            </Box>
          </Box>
        ) : (
          ''
        )}

        {isOpen && getHierarchyMap(hierarchyLevels)}
      </>
    );
  };
  const handleAddAttribute = (newAttribute?: any) => {
    if (
      dimensionPolicyData.attributes.length >= ATTR_MAX_COUNT ||
      filteredDimensionAttributes.length === 0 ||
      dimensionPolicyData.attributes.length >= dimensionAttributes.length
    ) {
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message:
            filteredDimensionAttributes.length === 0
              ? ALL_ATTR_LISTED_WARN
              : dimensionPolicyData.attributes.length >= dimensionAttributes.length
                ? ATTR_ROWS_CROSSED_MAX_AVAILABLE_WARN
                : ATTR_MAX_COUNT_WARN,
          severity: 'warning'
        })
      );
      return;
    }
    const attribute = {
      attribute: { key: 'select', displayName: '' },
      attributeAction: []
    };
    if (newAttribute?.attribute) {
      dispatch(setDimensionPolicyData('attributes', [...dimensionPolicyData.attributes, newAttribute]));
    } else {
      dispatch(setDimensionPolicyData('attributes', [...dimensionPolicyData.attributes, attribute]));
    }
  };

  const showAttributeSection = dimensionAttributeFF && dimensionAttributes !== null && dimensionAttributes.length > 0;
  return (
    <Grid container alignItems="flex-end" justifyContent="space-between">
      <Grid item xs={5} className={classes.formStyle}>
        <TextField
          id="policyName"
          name={POLICY_NAME}
          label={POLICY_NAME_LABEL}
          className={classes.textFieldSpacing}
          fullWidth
          variant="standard"
          value={dimensionPolicyData.policyName}
          onChange={handleOnChange}
          error={formErrorState.policyName !== ''}
          helperText={formErrorState.policyName}
          disabled={edit}
          required
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <TextField
          id="policyDescription"
          name={POLICY_DESCRIPTION}
          label={POLICY_DESC_LABEL}
          className={classes.textFieldSpacing}
          fullWidth
          variant="standard"
          value={dimensionPolicyData.policyDescription}
          onChange={handleOnChange}
          error={formErrorState.policyDescription !== ''}
          helperText={formErrorState.policyDescription}
          required
        />
      </Grid>
      <Grid item xs={5} className={classes.formStyle}>
        <LuiSelect
          maxHeight={DROPDOWN_MAX_HEIGHT}
          id="dimension-dropdown"
          onSelectionChange={onDimensionChange}
          itemData={dimensionsList}
          inputLabel={POLICY_DIMENSION_LABEL}
          variant="standard"
          selectedItem={dimensionPolicyData.dimension}
          width={'100%'}
          searchProps={{
            enableSearch: true,
            searchPlaceholder: 'Search dimension',
          }}
          classes={{ select: classes.dimensionDropdown }}
          data-testid="dimension-dropdown"
          required
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <LuiMultiSelectCustom
          customId="actions-multi-select-dimension"
          itemData={dimensionAccessActions?.sort(compareByProperty(DISPLAY_NAME))}
          itemKey="id"
          variant="standard"
          itemDisplayName="displayName"
          inputLabel={`${POLICY_ACTIONS_LABEL}`}
          selectedItems={dimensionPolicyData?.dimensionAction}
          selectAll
          selectAllDisplayText={SELECT_ALL}
          maxHeight={DROPDOWN_MAX_HEIGHT}
          onSelectionChange={(e: any) => handleActionChange('dimensionAction', e)}
          disabled={!dimensionPolicyData.dimension}
          data-testid="actions-multi-select-dimension-custom"
          required
        />
      </Grid>
      <Grid container alignItems="flex-start" className={classes.attributeSection}> 
      {showAttributeSection && (
        <Grid item xs={5} className={classes.formStyle}>
        <Typography variant="h4" component="span" className={classes.attributeHeading}>
          <Box id="dimension-attributes-title"  component="div">
            {`${DIMENSION_ATTRIBUTE_TITLE} (${OPTIONAL})`}
          </Box>
          <Typography variant="body2" component="div">
            {ATTR_MAX_COUNT_MSG}
          </Typography>
        </Typography>
      </Grid>)}
      {showAttributeSection && (
        <Grid item xs={7} className={classes.formStyle} >
          <Button
            name="add-dimension-attributes"
            disabled={!dimensionAttributes || dimensionAttributes.length === 0}
            onClick={handleAddAttribute}
            variant="outlined"
            color="primary"
            size="small"
          >
            {ADD_ATTRIBUTE_BTN_TEXT}
          </Button>
        </Grid>
      )}
      </Grid>
      {(showAttributeSection) ? (
        <Grid container>
          {dimensionPolicyData.attributes.map((attribute: any, i: number) => (
            <DimensionAttribute
              attribute={attribute}
              dimensionAttributes={filteredDimensionAttributes}
              actions={attributeAccessActions?.sort(compareByProperty(DISPLAY_NAME))}
              key={i}
              index={i}
            />
          ))}
        </Grid>
      ) : (dimensionAttributes !== null && dimensionAttributes.length === 0) ? <Grid item xs={12}><Alert severity="info">{NO_DIMENSION_ATTR}</Alert></Grid> : null}

      {dimensionPolicyData.hierarchy !== null ? (
        <Box width={'100%'}>
          <Grid item xs={12}>
            {hierarchyLevels?.length > 0 && renderHierarchySection()}
          </Grid>
          <Grid item xs={6} className={classes.formStyle}>
            {isOpen && (
              <LuiMultiSelectCustom
                customId="actions-multi-select"
                itemData={dataAccessActions?.sort(compareByProperty(DISPLAY_NAME))}
                itemKey="id"
                itemDisplayName="displayName"
                inputLabel={POLICY_ACTIONS_LABEL}
                selectedItems={dimensionPolicyData?.action}
                selectAll
                selectAllDisplayText={SELECT_ALL}
                maxHeight={DROPDOWN_MAX_HEIGHT}
                onSelectionChange={(e: any) => handleActionChange('action', e)}
                disabled={false}
                data-testid="actions-multi-select-custom"
              />
            )}
          </Grid>
        </Box>
      ) : dimensionPolicyData.hierarchyAll !== null ? (<>
        <Grid item xs={12} className={classes.formStyle}>
          <Typography variant="h4" component="span" className={classes.hierarchyTypographyTitle}>
            <Box fontWeight="fontWeightBold" component="div">
              {`${HIERARCHY_ACCESS_CONTROL} (${OPTIONAL})`}
            </Box>
          </Typography>
        </Grid>
        <Alert severity="info">{NO_AUTHORIZING_HIERARCHY}</Alert>
      </>) : null}
      {edit && !displayLoader && setIsSaveDisabled(false)}
      <Grid item xs={12}>
        {errors.info && <Alert severity="info">{errors.info}</Alert>}
      </Grid>
      {!errors.info && <ErrorMessage {...errors} />}
    </Grid>
  );
};

export default DimensionV2;